import type { ColumnDef, Row, Table } from '@tanstack/react-table';
import { ActionIcon, Group, NumberInput, Select, Text, TextInput, Tooltip } from '@mantine/core';
import { IconCheck, IconEdit, IconTrash, IconX } from '@tabler/icons-react';
import type { IEdicionFilasMeta } from './useEdicionFilas';

/** Columnas para tablas con edición en línea (usan la meta de useEdicionFilas). */

type Fila = { Id: string };
const estiloInput = { input: { fontSize: '12px', padding: '4px 8px' } };
const estiloTooltip = { fontSize: '10px', padding: '3px 5px' };

const metaDe = <T,>(table: Table<T>) => table.options.meta as IEdicionFilasMeta<T>;
const enEdicion = <T extends Fila>(row: Row<T>, table: Table<T>) => metaDe(table).filaEnEdicion === row.original.Id;
const estaVacio = (valor: unknown) => valor === null || valor === undefined || String(valor).trim() === '';

export interface FormatoNumero {
    decimales?: number;
    prefijo?: string;
    sufijo?: string;
    /** Sin separador de miles (años, códigos). */
    sinMiles?: boolean;
}

export const formatearNumero = (valor: number, { decimales = 0, prefijo = '', sufijo = '', sinMiles = false }: FormatoNumero) =>
    `${prefijo}${valor.toLocaleString('es-CO', { maximumFractionDigits: decimales, useGrouping: !sinMiles })}${sufijo}`;

export const columnaAcciones = <T extends Fila>(): ColumnDef<T> => ({
    id: 'actions',
    header: 'Acciones',
    size: 80,
    cell: ({ row, table }) => {
        const meta = metaDe(table);
        if (meta.soloLectura) return null;
        if (enEdicion(row, table)) {
            return (
                <Group gap={4} justify="center" wrap="nowrap">
                    <Tooltip label="Guardar" withinPortal zIndex={9999} style={estiloTooltip}>
                        <ActionIcon variant="subtle" color="var(--mantine-color-primary-0)" size="sm" radius="xl" onClick={() => meta.guardarFila(row.original.Id)}>
                            <IconCheck size={18} />
                        </ActionIcon>
                    </Tooltip>
                    <Tooltip label="Cancelar" withinPortal zIndex={9999} style={estiloTooltip}>
                        <ActionIcon variant="subtle" color="#df675a" size="sm" radius="xl" onClick={meta.cancelarEdicion}>
                            <IconX size={18} />
                        </ActionIcon>
                    </Tooltip>
                </Group>
            );
        }
        const bloqueado = meta.filaEnEdicion !== null;
        const textoBloqueado = 'Termina la edición actual';
        return (
            <Group gap={4} justify="center" wrap="nowrap">
                <Tooltip label={bloqueado ? textoBloqueado : 'Editar'} withinPortal zIndex={9999} style={estiloTooltip}>
                    <ActionIcon disabled={bloqueado} variant="transparent" color="var(--mantine-color-primary-0)" size="sm" radius="sm" onClick={() => meta.iniciarEdicion(row.original.Id)}>
                        <IconEdit size={18} />
                    </ActionIcon>
                </Tooltip>
                {meta.eliminarFila && (
                    <Tooltip label={bloqueado ? textoBloqueado : 'Eliminar'} withinPortal zIndex={9999} style={estiloTooltip}>
                        <ActionIcon disabled={bloqueado} variant="transparent" color="var(--mantine-color-primary-0)" size="sm" radius="sm" onClick={() => meta.eliminarFila?.(row.original.Id)}>
                            <IconTrash size={18} />
                        </ActionIcon>
                    </Tooltip>
                )}
            </Group>
        );
    }
});

/** Texto: se muestra como texto y se edita con un input. `obligatorio` marca el error al intentar guardar vacío. */
export const columnaTexto = <T extends Fila>(campo: keyof T & string, encabezado: string, obligatorio = true): ColumnDef<T> => ({
    accessorKey: campo,
    header: encabezado,
    cell: ({ row, table }) => {
        const valor = String(row.original[campo] ?? '');
        if (!enEdicion(row, table)) return <Text size="xs">{valor || '-'}</Text>;
        const meta = metaDe(table);
        return (
            <TextInput
                size="xs"
                maxLength={200}
                value={valor}
                placeholder={obligatorio ? `${encabezado} *` : encabezado}
                error={obligatorio && meta.intentoGuardar && estaVacio(valor)}
                onChange={(e) => meta.actualizarFila(row.original.Id, campo, e.currentTarget.value)}
                styles={estiloInput}
            />
        );
    }
});

/** Número: texto con formato fuera de edición, input numérico al editar la fila. */
export const columnaNumero = <T extends Fila>(campo: keyof T & string, encabezado: string, formato: FormatoNumero = {}, obligatorio = true): ColumnDef<T> => ({
    accessorKey: campo,
    header: encabezado,
    cell: ({ row, table }) => {
        const valor = row.original[campo] as number | string;
        if (!enEdicion(row, table)) {
            return <Text size="xs">{estaVacio(valor) ? '-' : formatearNumero(Number(valor), formato)}</Text>;
        }
        const meta = metaDe(table);
        return (
            <NumberInput
                size="xs"
                hideControls
                min={0}
                allowNegative={false}
                decimalScale={formato.decimales ?? 0}
                thousandSeparator={formato.sinMiles ? undefined : '.'}
                decimalSeparator=","
                prefix={formato.prefijo}
                suffix={formato.sufijo}
                value={valor}
                placeholder={obligatorio ? `${encabezado} *` : encabezado}
                error={obligatorio && meta.intentoGuardar && estaVacio(valor)}
                onChange={(v) => meta.actualizarFila(row.original.Id, campo, v === '' ? '' : Number(v))}
                styles={estiloInput}
            />
        );
    }
});

/** Lista desplegable: muestra la etiqueta de la opción y se edita con un Select. */
export const columnaSelect = <T extends Fila>(campo: keyof T & string, encabezado: string, opciones: { value: string; label: string }[], obligatorio = true): ColumnDef<T> => ({
    accessorKey: campo,
    header: encabezado,
    cell: ({ row, table }) => {
        const valor = String(row.original[campo] ?? '');
        if (!enEdicion(row, table)) return <Text size="xs">{opciones.find(o => o.value === valor)?.label ?? (valor || '-')}</Text>;
        const meta = metaDe(table);
        return (
            <Select
                size="xs"
                searchable
                data={opciones}
                value={valor || null}
                placeholder={obligatorio ? `${encabezado} *` : encabezado}
                error={obligatorio && meta.intentoGuardar && estaVacio(valor)}
                onChange={(v) => meta.actualizarFila(row.original.Id, campo, v ?? '')}
                comboboxProps={{ withinPortal: true, zIndex: 9999 }}
                styles={estiloInput}
            />
        );
    }
});

/** Valor calculado (de solo lectura) que no viene en la fila, p. ej. el subtotal que entrega el back. */
export const columnaCalculada = <T extends Fila>(id: string, encabezado: string, valor: (fila: T) => number | undefined, formato: FormatoNumero = {}): ColumnDef<T> => ({
    id,
    header: encabezado,
    cell: ({ row }) => {
        const resultado = valor(row.original);
        return <Text size="xs" fw={600}>{resultado === undefined ? '-' : formatearNumero(resultado, formato)}</Text>;
    }
});
